import type { ReactNode } from 'react';
import { Stack, Card, Title, Text, Group, Button, Badge, ThemeToggle } from '@repletoryreach/sdk-ui';
import { PageHeader, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { SegmentedControl, Switch } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { useAppStore, type Density, type TextScale, type ReducedMotion } from '@repletoryreach/sdk-utils/store';
import { palette } from '@repletoryreach/sdk-utils/color';
import { ColorSwatchPicker } from '../../../app/components/ColorSwatchPicker';
import { useT } from '../../../i18n/I18nProvider';
import './appearance.css';

/** What the console ships with, mirroring `main.tsx` and the store defaults. */
const DEFAULTS = {
  primary: palette.blue[600],
  density: 'comfortable' as Density,
  textScale: 'md' as TextScale,
  sidebarOpen: true,
  reducedMotion: 'auto' as ReducedMotion,
  highContrast: false
};

export default function SettingsAppearancePage() {
  const t = useT();
  const density = useAppStore((s) => s.density);
  const setDensity = useAppStore((s) => s.setDensity);
  const textScale = useAppStore((s) => s.textScale);
  const setTextScale = useAppStore((s) => s.setTextScale);
  const sidebarOpen = useAppStore((s) => s.sidebarOpen);
  const setSidebarOpen = useAppStore((s) => s.setSidebarOpen);
  const reducedMotion = useAppStore((s) => s.reducedMotion);
  const setReducedMotion = useAppStore((s) => s.setReducedMotion);
  const highContrast = useAppStore((s) => s.highContrast);
  const setHighContrast = useAppStore((s) => s.setHighContrast);
  const setPrimary = useAppStore((s) => s.setPrimary);

  /** A segment label with its consequence underneath, as the designs draw them. */
  const segment = (label: string, hint: string): ReactNode => (
    <span className="rr-appearance-segment">
      <span>{label}</span>
      <span className="rr-appearance-segment__hint">{hint}</span>
    </span>
  );

  function resetToDefaults() {
    openConfirmModal({
      title: t('settings.appearance.reset.title'),
      children: <p style={{ margin: 0 }}>{t('settings.appearance.reset.body')}</p>,
      labels: { confirm: t('settings.appearance.reset.confirm'), cancel: t('action.cancel') },
      onConfirm: () => {
        setPrimary(DEFAULTS.primary);
        setDensity(DEFAULTS.density);
        setTextScale(DEFAULTS.textScale);
        setSidebarOpen(DEFAULTS.sidebarOpen);
        setReducedMotion(DEFAULTS.reducedMotion);
        setHighContrast(DEFAULTS.highContrast);
      }
    });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('common.settings')}
        title={t('settings.appearance.title')}
        description={t('settings.appearance.description')}
      />

      {/*
        Seven settings, each one line long, used to be seven full-width cards
        stacked down two screens. Paired into two columns they fit one screen,
        which is what makes them comparable: an operator picking a density is
        also deciding a text scale.
      */}
      <div className="rr-appearance-grid">
        <SettingCard
          title={t('settings.appearance.theme.title')}
          description={t('settings.appearance.theme.description')}
          control={<ThemeToggle />}
        />

        <Card withBorder>
          <Stack gap="md">
            <div className="rr-appearance-row">
              <div className="rr-appearance-row__heading">
                <Stack gap="xs">
                  <Title order={5}>{t('settings.appearance.accent.title')}</Title>
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.appearance.accent.description')}</Text>
                </Stack>
              </div>
              <div className="rr-appearance-row__control"><ColorSwatchPicker /></div>
            </div>

            {/* The accent used to be judged from this page's own chrome alone,
                which shows almost none of it. These are the places it lands. */}
            <Stack gap="sm">
              <Group gap="sm" align="center">
                <Text role="overline" c="var(--rr-color-text-muted)">{t('settings.appearance.accent.preview')}</Text>
                <Badge variant="light" size="xs">{t('settings.appearance.accent.livePreview')}</Badge>
              </Group>
              <div className="rr-appearance-preview">
                <Group gap="sm" align="center" wrap="wrap">
                  <Button size="xs">{t('settings.appearance.accent.previewButton')}</Button>
                  <Button size="xs" variant="outline">{t('settings.appearance.accent.previewSecondary')}</Button>
                  <Badge variant="light">{t('settings.appearance.accent.previewBadge')}</Badge>
                  <StatusBadge status="healthy">{t('status.active')}</StatusBadge>
                </Group>
              </div>
            </Stack>
          </Stack>
        </Card>

        <SettingCard
          title={t('settings.appearance.density.title')}
          description={t('settings.appearance.density.description')}
          control={
            <SegmentedControl
              size="xs"
              value={density}
              onChange={(v) => setDensity(v as Density)}
              data={[
                { value: 'comfortable', label: segment(t('settings.appearance.density.comfortable'), t('settings.appearance.density.comfortableHint')) },
                { value: 'cozy', label: segment(t('settings.appearance.density.cozy'), t('settings.appearance.density.cozyHint')) },
                { value: 'compact', label: segment(t('settings.appearance.density.compact'), t('settings.appearance.density.compactHint')) }
              ]}
            />
          }
        />

        <SettingCard
          title={t('settings.appearance.textScale.title')}
          description={t('settings.appearance.textScale.description')}
          control={
            <SegmentedControl
              size="xs"
              value={textScale}
              onChange={(v) => setTextScale(v as TextScale)}
              data={[
                { value: 'sm', label: segment(t('settings.appearance.textScale.small'), t('settings.appearance.textScale.smallHint')) },
                { value: 'md', label: segment(t('settings.appearance.textScale.default'), t('settings.appearance.textScale.defaultHint')) },
                { value: 'lg', label: segment(t('settings.appearance.textScale.large'), t('settings.appearance.textScale.largeHint')) }
              ]}
            />
          }
        />

        <SettingCard
          title={t('settings.appearance.sidebar.title')}
          description={t('settings.appearance.sidebar.narrowNote')}
          control={
            <SegmentedControl
              size="xs"
              value={sidebarOpen ? 'full' : 'icon'}
              onChange={(v) => setSidebarOpen(v === 'full')}
              data={[
                { value: 'icon', label: segment(t('settings.appearance.sidebar.iconOnly'), t('settings.appearance.sidebar.iconOnlyHint')) },
                { value: 'full', label: segment(t('settings.appearance.sidebar.full'), t('settings.appearance.sidebar.fullHint')) }
              ]}
            />
          }
        />

        <SettingCard
          title={t('settings.appearance.motion.title')}
          description={t('settings.appearance.motion.description')}
          control={
            <SegmentedControl
              size="xs"
              value={reducedMotion}
              onChange={(v) => setReducedMotion(v as ReducedMotion)}
              data={[
                { value: 'auto', label: segment(t('settings.appearance.motion.auto'), t('settings.appearance.motion.autoHint')) },
                { value: 'always', label: segment(t('settings.appearance.motion.always'), t('settings.appearance.motion.alwaysHint')) },
                { value: 'never', label: segment(t('common.never'), t('settings.appearance.motion.neverHint')) }
              ]}
            />
          }
        />

        <SettingCard
          title={t('settings.appearance.contrast.title')}
          description={t('settings.appearance.contrast.description')}
          control={
            <Switch
              aria-label={t('settings.appearance.motion.highContrast')}
              checked={highContrast}
              onChange={(v) => setHighContrast(v)}
            />
          }
          footnote={t('settings.appearance.contrast.switchHint')}
        />
      </div>

      {/*
        An operator who sets Compact on a laptop and opens the console on a
        workstation assumed the setting had failed to save. It saves; it just
        saves here. That used to be a full-width warning banner above the
        settings, which is more alarm than a browser preference deserves.
      */}
      <div className="rr-appearance-footer">
        <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.appearance.localOnlyNotice')}</Text>
        <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.appearance.autosaved')}</Text>
        <Button variant="default" onClick={resetToDefaults}>{t('settings.appearance.reset.action')}</Button>
      </div>
    </Stack>
  );
}

function SettingCard({ title, description, control, footnote }: {
  title: string;
  description: string;
  control: ReactNode;
  /** A second muted line under the row, where one line cannot carry the caveat. */
  footnote?: string;
}) {
  return (
    <Card withBorder>
      <Stack gap="sm">
        <div className="rr-appearance-row">
          <div className="rr-appearance-row__heading">
            <Stack gap="xs">
              <Title order={5}>{title}</Title>
              <Text role="caption" c="var(--rr-color-text-muted)">{description}</Text>
            </Stack>
          </div>
          <div className="rr-appearance-row__control">{control}</div>
        </div>
        {footnote && <Text role="caption" c="var(--rr-color-text-muted)">{footnote}</Text>}
      </Stack>
    </Card>
  );
}
